/**
 * Timeline Component
 * Simple scroll-based detection: whichever row is closest to viewport center shows its image
 * With min-height: 80vh on rows, ensures smooth transitions on all viewport sizes
 */

(() => {
  'use strict';

  const selectors = {
    timelineRows: '[data-timeline-rows]',
    timelineRow: '[data-timeline-row]',
    timelineRowEditor: '[data-timeline-row-editor]',
    timelineButton: '[data-timeline-button]',
    blockScroll: '[data-block-scroll]',
  };

  const classes = {
    isSelected: 'is-selected',
  };

  if (customElements.get('timeline-component')) return;

  customElements.define(
    'timeline-component',
    class TimelineComponent extends HTMLElement {
      constructor() {
        super();

        this.rows = this.querySelectorAll(selectors.timelineRow);
        if (this.rows.length < 2) return;

        this.rowsWidth = 0;
        this.rowsHeight = [];
        this.buttons = this.querySelectorAll(selectors.timelineButton);
        this.rowsHolder = this.querySelector(selectors.timelineRows);
        this.isDesktopView = !window.theme.isMobile();
        this.scrollRAF = null;

        this.resizeEvent = () => this.resizeEvents();
        this.scrollEvent = () => this.handleScroll();
      }

      connectedCallback() {
        this.calculateRowsDimensions();
        this.setupButtonHandlers();

        if (this.isDesktopView) {
          document.addEventListener('theme:scroll', this.scrollEvent);
          // Initial update
          this.updateDesktopTimeline();
        } else {
          this.rowsHolder.addEventListener('scroll', this.scrollEvent);
        }

        document.addEventListener('theme:resize:width', this.resizeEvent);
      }

      handleScroll() {
        if (!this.scrollRAF) {
          this.scrollRAF = requestAnimationFrame(() => {
            if (this.isDesktopView) {
              this.updateDesktopTimeline();
            } else {
              this.updateMobileTimeline();
            }
            this.scrollRAF = null;
          });
        }
      }

      updateDesktopTimeline() {
        const viewportHeight = window.innerHeight;
        const viewportCenter = window.scrollY + (viewportHeight / 2);

        // Find which row is closest to viewport center
        let activeIndex = 0;
        let smallestDistance = Infinity;

        this.rows.forEach((row, index) => {
          const rect = row.getBoundingClientRect();
          const rowTop = window.scrollY + rect.top;
          const rowCenter = rowTop + (rect.height / 2);
          const distance = Math.abs(rowCenter - viewportCenter);

          if (distance < smallestDistance) {
            smallestDistance = distance;
            activeIndex = index;
          }
        });

        // Calculate progress for continuous line
        const firstRect = this.rows[0].getBoundingClientRect();
        const lastRect = this.rows[this.rows.length - 1].getBoundingClientRect();

        const firstTop = window.scrollY + firstRect.top;
        const lastTop = window.scrollY + lastRect.top;
        const totalRange = lastTop - firstTop;

        // Calculate overall scroll progress (0-100%)
        const currentProgress = viewportCenter - firstTop;
        const overallProgress = totalRange > 0
          ? Math.max(0, Math.min(100, (currentProgress / totalRange) * 100))
          : 0;

        // Build array of row positions for stable calculations
        const rowPositions = Array.from(this.rows).map((row) => {
          const rect = row.getBoundingClientRect();
          return window.scrollY + rect.top;
        });

        // Update each row
        this.rows.forEach((row, index) => {
          const rowTop = rowPositions[index];
          const rowPositionPercent = totalRange > 0
            ? ((rowTop - firstTop) / totalRange) * 100
            : 0;

          let progress = 0;

          if (index === 0) {
            // First row: fills from 0% to this row's position
            if (overallProgress >= rowPositionPercent) {
              progress = 100;
            } else if (rowPositionPercent > 0) {
              progress = (overallProgress / rowPositionPercent) * 100;
            }
          } else {
            // Other rows: fill from previous row to this row
            const prevTop = rowPositions[index - 1];
            const prevPositionPercent = totalRange > 0
              ? ((prevTop - firstTop) / totalRange) * 100
              : 0;

            if (overallProgress >= rowPositionPercent) {
              // We've scrolled past this row - keep at 100%
              progress = 100;
            } else if (overallProgress >= prevPositionPercent) {
              // Between previous row and this row - show partial progress
              const segmentSize = rowPositionPercent - prevPositionPercent;
              const segmentProgress = overallProgress - prevPositionPercent;
              progress = segmentSize > 0 ? (segmentProgress / segmentSize) * 100 : 0;
            } else {
              // Haven't reached previous row yet - stay at 0%
              progress = 0;
            }
          }

          row.style.setProperty('--percent-desktop', `${Math.max(0, Math.min(100, progress))}%`);

          // Activate the closest row AND all previous rows (to show filled black lines)
          const isActive = index <= activeIndex;
          row.classList.toggle(classes.isSelected, isActive);
        });
      }

      updateMobileTimeline() {
        const {scrollLeft, offsetWidth: containerWidth} = this.rowsHolder;
        const scrollableWidth = this.rowsWidth - containerWidth;

        if (scrollableWidth <= 0) return;

        const mobileScrollPercent = (scrollLeft / scrollableWidth) * 100;
        this.style.setProperty('--percent-mobile', `${mobileScrollPercent}%`);

        this.rows.forEach((row) => {
          const rowLeftPercent = (row.offsetLeft / this.rowsWidth) * 100;
          const isActive = mobileScrollPercent >= rowLeftPercent;
          row.classList.toggle(classes.isSelected, isActive);
        });
      }

      calculateRowsDimensions() {
        if (!this.rows.length) return;

        this.rowsHeight = [];
        this.rowsWidth = 0;
        let cumulativeHeight = 0;

        this.rows.forEach((row, index) => {
          this.rowsHeight.push(row.offsetHeight);
          this.rowsWidth += row.offsetWidth + parseInt(getComputedStyle(row).marginRight);

          if (window.Shopify.designMode) {
            const editors = this.querySelectorAll(selectors.timelineRowEditor);
            if (editors.length) {
              cumulativeHeight += row.offsetHeight;
              editors[index]?.style.setProperty('--row-height-min', `${row.offsetHeight}px`);
              editors[index + 1]?.style.setProperty('--row-top-mobile', `${cumulativeHeight}px`);
            }
          }
        });
      }

      setupButtonHandlers() {
        if (!this.buttons.length) return;

        this.buttons.forEach((button, index) => {
          button.addEventListener('click', (e) => {
            e.preventDefault();

            if (window.theme.isMobile()) {
              this.scrollToRowMobile(index);
            } else {
              this.scrollToRowDesktop(index);
            }
          });
        });
      }

      scrollToRowMobile(index) {
        const row = this.rows[index];
        if (!row) return;

        const scrollLeft = row.offsetLeft - 16;
        this.rowsHolder.scrollTo({
          left: scrollLeft,
          behavior: 'smooth',
        });
      }

      scrollToRowDesktop(index) {
        const row = this.rows[index];
        if (!row) return;

        // Scroll so row's center is at viewport center
        const rowRect = row.getBoundingClientRect();
        const rowTop = window.scrollY + rowRect.top;
        const rowCenter = rowTop + (rowRect.height / 2);
        const targetScroll = rowCenter - (window.innerHeight / 2);

        window.scrollTo({
          top: targetScroll,
          behavior: 'smooth',
        });
      }

      resizeEvents() {
        this.calculateRowsDimensions();

        const isDesktop = !window.theme.isMobile();

        // Switch between desktop/mobile
        if (isDesktop && !this.isDesktopView) {
          // Switched to desktop
          this.isDesktopView = true;
          this.rowsHolder.removeEventListener('scroll', this.scrollEvent);
          document.addEventListener('theme:scroll', this.scrollEvent);
          this.updateDesktopTimeline();
        } else if (!isDesktop && this.isDesktopView) {
          // Switched to mobile
          this.isDesktopView = false;
          document.removeEventListener('theme:scroll', this.scrollEvent);
          this.rowsHolder.addEventListener('scroll', this.scrollEvent);
        }
      }

      disconnectedCallback() {
        this.rowsHolder.removeEventListener('scroll', this.scrollEvent);
        document.removeEventListener('theme:scroll', this.scrollEvent);
        document.removeEventListener('theme:resize:width', this.resizeEvent);

        if (this.scrollRAF) {
          cancelAnimationFrame(this.scrollRAF);
        }
      }

      onBlockSelect(e) {
        const blockScroll = this.querySelector(selectors.blockScroll);
        if (!blockScroll) return;

        const block = e.srcElement;
        const index = [...block.parentElement.children].indexOf(block);
        const row = this.rows[index];

        if (row) {
          blockScroll.scrollTo({
            top: 0,
            left: row.offsetLeft,
            behavior: 'smooth',
          });
        }
      }
    }
  );
})();
